import React from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../../providers/theme/colors/colors";
import {
  deaneryList,
  type DeaneryOption,
  type ParishOption,
} from "../../../utils/deanery_parishes";
import { sanitizePhone } from "../data";

type DycRegistrationFormProps = {
  selectedDeanery: DeaneryOption | null;
  selectedParish: ParishOption | null;
  parishOptions: ParishOption[];
  presidentName: string;
  animatorName: string;
  presidentContactNumber: string;
  animatorContactNumber: string;
  presidentEmail: string;
  totalParticipantsCount: string;
  onDeaneryChange: (value: DeaneryOption | null) => void;
  onParishChange: (value: ParishOption | null) => void;
  onPresidentNameChange: (value: string) => void;
  onAnimatorNameChange: (value: string) => void;
  onPresidentContactNumberChange: (value: string) => void;
  onAnimatorContactNumberChange: (value: string) => void;
  onPresidentEmailChange: (value: string) => void;
  onTotalParticipantsCountChange: (value: string) => void;
  showTotalParticipantsCount?: boolean;

  // Payment Details Props
  paymentPayerName?: string;
  paymentDate?: string;
  paymentAmount?: string;
  paymentScreenshot?: string;
  paymentScreenshotName?: string;
  onPaymentPayerNameChange?: (value: string) => void;
  onPaymentDateChange?: (value: string) => void;
  onPaymentAmountChange?: (value: string) => void;
  onPaymentScreenshotChange?: (dataUrl: string, fileName: string) => void;
  showPaymentDetails?: boolean;
  onScrollToPaymentDetails?: () => void;
};

const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 800;
        const scaleSize = MAX_WIDTH / img.width;
        const width = img.width > MAX_WIDTH ? MAX_WIDTH : img.width;
        const height = img.width > MAX_WIDTH ? img.height * scaleSize : img.height;

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.7));
        } else {
          resolve(event.target?.result as string);
        }
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

export default function DycRegistrationForm(props: DycRegistrationFormProps) {
  const {
    selectedDeanery,
    selectedParish,
    parishOptions,
    presidentName,
    animatorName,
    presidentContactNumber,
    animatorContactNumber,
    presidentEmail,
    totalParticipantsCount,
    onDeaneryChange,
    onParishChange,
    onPresidentNameChange,
    onAnimatorNameChange,
    onPresidentContactNumberChange,
    onAnimatorContactNumberChange,
    onPresidentEmailChange,
    onTotalParticipantsCountChange,
    showTotalParticipantsCount = true,
    paymentPayerName = "",
    paymentDate = "",
    paymentAmount = "",
    paymentScreenshot = "",
    paymentScreenshotName = "",
    onPaymentPayerNameChange,
    onPaymentDateChange,
    onPaymentAmountChange,
    onPaymentScreenshotChange,
    showPaymentDetails = true,
    onScrollToPaymentDetails,
  } = props;

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onPaymentScreenshotChange) {
      try {
        const compressedDataUrl = await compressImage(file);
        onPaymentScreenshotChange(compressedDataUrl, file.name);
      } catch (e) {
        // Fallback
        const reader = new FileReader();
        reader.onloadend = () => {
          onPaymentScreenshotChange(reader.result as string, file.name);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  return (
    <Card sx={{ borderRadius: 4, border: `1px solid ${MAIN_THEME_COLOR}22` }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={deaneryList}
              value={selectedDeanery}
              onChange={(_, value) => onDeaneryChange(value)}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label="Deanery" required />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={parishOptions}
              value={selectedParish}
              onChange={(_, value) => onParishChange(value)}
              getOptionLabel={(option) => option.value}
              disabled={!parishOptions.length}
              renderInput={(params) => <TextField {...params} label="Parish" required />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="President Name"
              value={presidentName}
              onChange={(event) => onPresidentNameChange(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Animator Name"
              value={animatorName}
              onChange={(event) => onAnimatorNameChange(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="President Contact Number"
              value={presidentContactNumber}
              onChange={(event) =>
                onPresidentContactNumberChange(sanitizePhone(event.target.value))
              }
              inputProps={{
                inputMode: "numeric",
                maxLength: 15,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Animator Contact Number"
              value={animatorContactNumber}
              onChange={(event) =>
                onAnimatorContactNumberChange(sanitizePhone(event.target.value))
              }
              inputProps={{
                inputMode: "numeric",
                maxLength: 15,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="President Email"
              type="email"
              value={presidentEmail}
              onChange={(event) => onPresidentEmailChange(event.target.value)}
            />
          </Grid>
          {showTotalParticipantsCount ? (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Total Participants Count (All Unit Participants)"
                helperText="Includes all participants from your unit, including those not participating in any competition"
                value={totalParticipantsCount}
                onChange={(event) =>
                  onTotalParticipantsCountChange(event.target.value.replace(/\D/g, ""))
                }
                inputProps={{
                  inputMode: "numeric",
                  min: 1,
                  max: 100,
                }}
              />
            </Grid>
          ) : null}

          {showPaymentDetails ? (
            <Grid item xs={12}>
              <Box sx={{ mt: 1, pt: 2, borderTop: `1px solid ${MAIN_THEME_COLOR}15` }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  mb={1}
                  gap={1}
                >
                  <Typography variant="h6" fontWeight={800} color={MAIN_THEME_COLOR}>
                    Payment Details (Compulsory)
                  </Typography>
                  {onScrollToPaymentDetails && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={onScrollToPaymentDetails}
                      sx={{
                        color: SECONDARY_THEME_COLOR,
                        fontWeight: 700,
                        textTransform: "none",
                        textDecoration: "underline",
                        p: 0,
                        "&:hover": {
                          textDecoration: "underline",
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      View QR Code & payment details above ↑
                    </Button>
                  )}
                </Stack>
                <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
                  <strong>Note:</strong> All payments must be made online. Please fill out your payment details below.
                </Alert>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Name of Person Who Made Payment"
                      value={paymentPayerName}
                      onChange={(e) => onPaymentPayerNameChange?.(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      type="date"
                      label="Date of Payment"
                      value={paymentDate}
                      onChange={(e) => onPaymentDateChange?.(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Select payment date (DD/MM/YYYY)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Amount Paid (₹)"
                      value={paymentAmount}
                      onChange={(e) => onPaymentAmountChange?.(e.target.value)}
                      inputProps={{ min: 250, max: 100000 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        Upload Payment Screenshot *
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          sx={{ borderRadius: 3, textTransform: "none" }}
                        >
                          Choose Screenshot Image
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleFileUpload}
                          />
                        </Button>
                        {paymentScreenshotName ? (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ImageOutlinedIcon color="primary" fontSize="small" />
                            <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                              {paymentScreenshotName}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="error">
                            No file selected
                          </Typography>
                        )}
                      </Stack>
                      {paymentScreenshot ? (
                        <Box sx={{ mt: 1 }}>
                          <img
                            src={paymentScreenshot}
                            alt="Payment Screenshot Preview"
                            style={{
                              maxHeight: 90,
                              borderRadius: 8,
                              border: "1px solid #ccc",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      ) : null}
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ) : null}
        </Grid>
      </CardContent>
    </Card>
  );
}

